import * as Yup from "yup";
import { isUSA } from "../../common/utils";

export const alphaNumericRegex = /^[a-zA-Z0-9](?:[a-zA-Z0-9 -]*[a-zA-Z0-9])?$/;
export const addressUsZipRegEx = /^\s*\d{5}(?:-\d{4})?\s*$/;
export const zipIsRequired = "ZIP / Postal code is required";
export const usZipSyntaxError = "ZIP / Postal code must have format ##### or #####-####";
export const alphanumericError = "Must contain only alpha-numeric characters and spaces";

export const AddressInputSchemaGenerator = ({
  testIsUSA = isUSA,
  zipRequiredForNonUS = true,
}: {
  testIsUSA?: (country: string | null | undefined) => boolean;
  zipRequiredForNonUS?: boolean;
} = {}) =>
  Yup.object().shape({
    sameAsMailingAddress: Yup.bool(),
    streetLine1: Yup.string().when("sameAsMailingAddress", {
      is: (value: boolean) => !value,
      then: (schema) =>
        schema.required("Street is required").matches(/\S/, "Street cannot be just spaces"),
    }),
    streetLine2: Yup.string(),
    city: Yup.string().when("sameAsMailingAddress", {
      is: (value: boolean) => !value,
      then: (schema) =>
        schema.required("City is required").matches(/\S/, "City cannot be just spaces"),
    }),
    state: Yup.string().when(["country", "sameAsMailingAddress"], {
      is: (country: string, sameAsMailingAddress: boolean) =>
        testIsUSA(country) && !sameAsMailingAddress,
      then: (schema) => schema.required("State is required"),
      otherwise: (schema) => schema,
    }),
    province: Yup.string().max(60, "Province must be 60 characters or less"),
    zip: Yup.string().when(
      ["country", "sameAsMailingAddress"],
      ([country, sameAsMailingAddress], schema) => {
        let zipSchema = schema;
        const isUS = testIsUSA(country as string);
        if (isUS) {
          zipSchema = zipSchema.matches(addressUsZipRegEx, usZipSyntaxError);
        } else {
          // For non-US, allow empty strings (when optional) or valid alphanumeric postal codes
          zipSchema = zipSchema.matches(alphaNumericRegex, {
            message: alphanumericError,
            excludeEmptyString: true,
          });
        }
        const shouldRequireZip = !sameAsMailingAddress && (isUS || zipRequiredForNonUS);
        if (shouldRequireZip) {
          zipSchema = zipSchema.required(zipIsRequired);
        }
        return zipSchema;
      },
    ),
    country: Yup.string().when("sameAsMailingAddress", {
      is: (value: boolean) => !value,
      then: (schema) => schema.required("Country is required"),
    }),
  });

export const AddressInputSchema = AddressInputSchemaGenerator();

export const PaymentInstructionAddressSchema = AddressInputSchemaGenerator({
  zipRequiredForNonUS: false,
});

export type AddressInputType = Yup.InferType<typeof AddressInputSchema>;
